"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.applySettings = exports.buildAggregation = exports.sendAggregation = exports.connectAggregationToState = exports.connectAggregationToObservable = exports.asyncMutateResponse = void 0;
const tslib_1 = require("tslib");
const request_1 = require("./request");
const param_stream_1 = require("../param-stream/param-stream");
const aggregations_1 = require("@pendo/aggregations");
const isFunction_1 = tslib_1.__importDefault(require("lodash/isFunction"));
const isObject_1 = tslib_1.__importDefault(require("lodash/isObject"));
const each_1 = tslib_1.__importDefault(require("lodash/each"));
const defaultsDeep_1 = tslib_1.__importDefault(require("lodash/defaultsDeep"));
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const uuid_1 = require("uuid");
const responseFormat = tslib_1.__importStar(require("./response-format"));
const loading_states_const_1 = require("../../constants/loading-states.const");
const status_1 = require("./status");
const utils_1 = require("./utils");
function formatResponseValue(value, format = 'default') {
    switch (format) {
        case 'default':
            return value;
        case 'rows':
            return responseFormat.getRows(value);
        case 'row':
            return responseFormat.getRow(value);
        case 'number':
            return responseFormat.getNumber(value);
        case 'highcharts':
            return responseFormat.getHighchartsData(value);
        default:
            return value;
    }
}
function asyncMutateResponse(aggregation, callback) {
    return {
        ...aggregation,
        asyncMutateResponse: callback
    };
}
exports.asyncMutateResponse = asyncMutateResponse;
function connectAggregationToObservable(aggregation, observable, config) {
    const streamId = (0, uuid_1.v4)();
    return observable.pipe((0, operators_1.switchMap)((paramsOrStatus) => {
        return (0, status_1.isStatus)(paramsOrStatus)
            ? (0, rxjs_1.of)(paramsOrStatus)
            : sendAggregation(aggregation, streamId, paramsOrStatus, config);
    }), (0, operators_1.catchError)((error) => {
        return (0, rxjs_1.of)((0, status_1.createRejectedStatus)(error));
    }));
}
exports.connectAggregationToObservable = connectAggregationToObservable;
function connectAggregationToState(aggregation, ...convertersOrObservables) {
    return (sourcesOfState, config = {}) => {
        const param$ = (0, param_stream_1.connectToState)(...convertersOrObservables)(sourcesOfState);
        return connectAggregationToObservable(aggregation, param$, config);
    };
}
exports.connectAggregationToState = connectAggregationToState;
function sendAggregation(aggObject, streamId, params, config) {
    errorOnFunctionParameters(aggObject.name, params);
    const { aggregation, asyncMutateResponse, responseFormat } = buildAggregation(aggObject, streamId, params);
    const response$ = (0, request_1.queueAggregationRequest)(aggregation, config);
    return response$.pipe((0, operators_1.mergeMap)(async (response) => {
        if (response.status === loading_states_const_1.LOADING_STATES.RESOLVED) {
            let formattedValue = formatResponseValue(response.value, responseFormat);
            if ((0, isFunction_1.default)(asyncMutateResponse)) {
                formattedValue = await asyncMutateResponse(formattedValue);
            }
            return {
                ...response,
                value: formattedValue
            };
        }
        return response;
    }));
}
exports.sendAggregation = sendAggregation;
function buildAggregation(aggObject, streamId, params) {
    const { name, productArea, subProductArea, displayName, responseFormat, build, asyncMutateResponse, response, requestId, settings, mockRequestId, overrideRequestIdInTestEnv } = aggObject;
    if (!productArea && !(0, utils_1.isProduction)()) {
        // eslint-disable-next-line no-console
        console.warn(`Aggregation "${name}" is missing a productArea`);
    }
    const pipeline = build(params);
    const reactiveSettings = (0, defaultsDeep_1.default)(settings, pipeline.settings || {});
    const aggName = (0, isFunction_1.default)(name) ? name(params) : name;
    const aggLabelObject = {
        name: aggName,
        productArea,
        subProductArea
    };
    let aggregation;
    if (aggObject.multiAgg) {
        aggregation = {
            ...aggLabelObject,
            requests: pipeline
        };
    }
    else {
        aggregation = aggregations_1.operators.aggregation(aggLabelObject, pipeline);
    }
    const isTestEnv = window.Cypress && overrideRequestIdInTestEnv;
    Object.assign(aggregation, {
        streamId,
        params,
        // used when FE needs a more unique name because `name` can't be super unique (BE groups on `name` to look at performance),
        displayName: (0, isFunction_1.default)(displayName) ? displayName(params) : displayName || undefined,
        // only needed by CSVs/paths/funnels/any agg that has a funky response type that pulls from GCS
        response: (0, isFunction_1.default)(response) ? response(params) : response || undefined,
        requestId: (0, isFunction_1.default)(requestId) ? requestId(params) : requestId || undefined,
        settings: reactiveSettings,
        // needed for cypress stubbed responses
        mockRequestId: mockRequestId || (isTestEnv ? name : undefined)
    });
    return {
        aggregation,
        asyncMutateResponse,
        responseFormat
    };
}
exports.buildAggregation = buildAggregation;
function applySettings(agg, settings) {
    agg.settings = (0, defaultsDeep_1.default)(agg.settings || {}, settings);
    return agg;
}
exports.applySettings = applySettings;
function complain(complaint) {
    if ((0, utils_1.isProduction)())
        return;
    // eslint-disable-next-line no-console
    console.error(complaint);
    throw new Error(complaint);
}
function errorOnFunctionParameters(name, params, keyPath = '', depth = 0) {
    if ((0, utils_1.isProduction)() || depth > 3)
        return;
    (0, each_1.default)(params, (param, key) => {
        const path = keyPath ? `${keyPath} --> ${key}` : key;
        if ((0, isFunction_1.default)(param)) {
            complain(`Using functions in aggregation parameters is prohibited. Aggregation: ${name}, parameter: ${path}`);
        }
        if ((0, isObject_1.default)(param)) {
            errorOnFunctionParameters(name, param, path, depth + 1);
        }
    });
}
